import {useRef, useEffect, useState} from 'react';
import get from 'lodash/get';
import omit from 'lodash/omit';
import PropTypes from 'prop-types';
import {useNavigate} from 'react-router-dom';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import FormGroup from '@mui/material/FormGroup';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';
import Link from '@mui/material/Link';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import config from '../../../config/config';
import CustomTextField from '../../common/CustomTextField';
import * as formValidations from '../../../helpers/validations';
import FormLayout from "../../patientSignup/FormLayout";
import './NewAccountForm.scss';

const defaultFormValues = {
  firstName:'',
  lastName: '',
  email: '',
  password: '',
  agreedToTerms: false,
};

const validations = {
  firstName: str => formValidations.name(str) && str.length > 0,
  lastName: str => formValidations.name(str) && str.length > 0,
  email: formValidations.email,
  password: formValidations.password,
  agreedToTerms: bool => !!bool,
};

const handleFieldUpdate = (formValues, setFormValues) => fieldName => e => {
  const value = (
    fieldName === 'agreedToTerms'
    ? get(e, 'target.checked')
    : get(e, 'target.value', '').replace(/^\s/g, '').replace(/\s$/g, '')
  );
  const values = {
    ...formValues,
    [fieldName]: fieldName === 'email' ? value.toLowerCase() : value,
  };
  return setFormValues(values);
};

const checkIfFormIsValid = (
  formValues,
  validationDict,
  turnstileToken,
) => {
  if (!turnstileToken) return false;
  const containsInvalidValues = Object.keys(validationDict).reduce((acc, k) => (
    validationDict[k] ? acc : false
  ), true);
  return containsInvalidValues;
};

const TURNSTILE_RESET_INTERVAL_MS = 4 * 60 * 1000;

const useTurnstile = ({setTurnstileToken}) => {
  const siteKey = config().turnstilePublicKey
  const turnstile = window?.turnstile;
  useEffect(() => {
    if (!turnstile || !siteKey) return;
    const widgetId = turnstile.render("#turnstile-container", {
      // Always pass: 1x00000000000000000000AA
      // Force a challenge: '3x00000000000000000000FF'
      // Always block: '2x00000000000000000000AB'
      sitekey: siteKey,
      callback: (token) => {
        setTurnstileToken(token);
      },
    });
    // tokens expire after ~5 minutes, so refresh proactively before that happens
    const intervalId = setInterval(() => {
      setTurnstileToken(null);
      turnstile.reset(widgetId);
    }, TURNSTILE_RESET_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [turnstile, siteKey]);
};

const NewAccountForm = props => {
  const {onSubmit, setNumSteps} = props;
  const [formValues, setFormValues] = useState(defaultFormValues);
  const [hasGuardian, setHasGuardian] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [blurred, setBlurred] = useState({});
  const [guardianDialogOpen, setGuardianDialogOpen] = useState(false);
  const navigate = useNavigate();
  useTurnstile({setTurnstileToken});
  const isProvider = get(props, 'isProvider', false);

  const validationDict = Object.keys(validations).reduce((acc, k) => ({
    ...acc,
    [k]: validations[k](formValues[k])
  }), {});

  const onChangeHandler = handleFieldUpdate(formValues, setFormValues);
  const formIsValid = checkIfFormIsValid(
    formValues,
    validationDict,
    turnstileToken,
  );
  return (
    <FormLayout
      className="NewAccountForm"
      gap='16px'
      marginTop={{xs: '16px', sm: '0'}}
    >
      <Stack direction='row' justifyContent='center'>
        <Typography 
          className="already-have-account" 
          variant='small' 
          fontWeight={700}
          color='text.light' 
          fontSize='0.9rem'
        >
          Already started your account registration?
          {' '}
          <Link
            style={{cursor: 'pointer'}}
            onClick={() => navigate('/login')}
          >
            Login
          </Link>
        </Typography>
      </Stack>
      <Stack
        direction='row' gap={{md: 4.5, xs: 3}}
        justifyContent='space-between'
        flexWrap={{sm: 'nowrap', xs: 'wrap'}}
      >
        <CustomTextField
          id='firstNameInput'
          placeholder='Jane'
          variant='outlined'
          label={hasGuardian ? 'Dependent First Name': 'First Name'}
          required
          fullWidth
          error={!validationDict.firstName && blurred.firstName}
          onBlur={() => setBlurred({...blurred, firstName: true})}
          onChange={onChangeHandler('firstName')}
        />

        <CustomTextField
          id='lastNameInput'
          placeholder='Smith'
          variant='outlined'
          label={hasGuardian ? 'Dependent Last Name': 'Last Name'}
          required
          fullWidth
          error={!validationDict.lastName && blurred.lastName}
          onBlur={() => setBlurred({...blurred, lastName: true})}
          onChange={onChangeHandler('lastName')}
        />
      </Stack>
      <CustomTextField
        id='emailInput'
        placeholder='jane.smith@gmail.com'
        variant='outlined'
        label='Email'
        required
        type='email'
        error={!validationDict.email && blurred.email}
        onBlur={() => setBlurred({...blurred, email: true})}
        onChange={onChangeHandler('email')}
      />
      <CustomTextField
        id='passwordInput'
        placeholder='Enter a password'
        variant='outlined'
        label='Password'
        required
        type='password'
        error={!validationDict.password && blurred.password}
        onBlur={() => setBlurred({...blurred, password: true})}
        onChange={onChangeHandler('password')}
        helperText={
          !validationDict.password
          ? 'Password must be at least 8 characters and contain an uppercase letter, a lowercase letter, and a number.'
          : null
        }
        helperTextClass='password-helper-text'
      />
      {
        isProvider
        ? <></>
        : <>
          <FormControl sx={{marginTop: '8px'}}>
            <FormLabel
              id='NewAccountForm-radio-buttons-group-label'
              className='NewAccountForm__guardian-radio-label'
              sx={{padding: '7.5px 7.5px 3px 7.5px !important'}}
            >
              <Typography variant='large500Tight' color='text.primary'>Are you signing up for a dependent?</Typography>
            </FormLabel>

            <RadioGroup
              sx={{
                padding: '2px 7.5px !important',
                gap: '41px'
              }}
              row
              aria-labelledby="row-radio-buttons-group-label"
              name="row-radio-buttons-group"
              value={hasGuardian}
              onChange={e => {
                const bool = !(e.target.value !== 'true');
                setHasGuardian(bool);
                if (bool) {
                  setGuardianDialogOpen(true);
                  setNumSteps(8);
                }else{
                  setNumSteps(7);
                }
              }}
            >
              <FormControlLabel
                sx={{width: '44px', height: '23px', gap:'7.5px', margin: '0 !important'}}
                value="false" control={<Radio size='small' sx={{width: '18px', height: '18px'}}/>}
                label={<Typography variant='large300Loose'>No</Typography>}
              />
              <FormControlLabel
                sx={{width: '44px', height: '23px', gap:'7.5px', margin: '0 !important'}}
                value="true" control={<Radio size='small' sx={{width: '18px', height: '18px'}}/>}
                label={<Typography variant='large300Loose'>Yes</Typography>}
              />
            </RadioGroup>

            <FormHelperText
              sx={{margin: '0 !important', padding: '3px 0 3px 7.5px !important', lineHeight: '1'}}
            >
              <Typography variant='small500' fontWeight={500}  color='text.light' sx={{wordSpacing: '0.8px'}}>
                Selecting yes will prompt you to enter guardian information on the next screen.
              </Typography>
            </FormHelperText>
          </FormControl>

          <Dialog
            open={guardianDialogOpen}
            onClose={() => setGuardianDialogOpen(false)}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
          >
            <DialogContent>
              Please be sure to enter the dependent's First and Last Name on this screen. You will be prompted to enter your Guardian information on the following screen.
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setGuardianDialogOpen(false)}>
                Okay
              </Button>
            </DialogActions>
          </Dialog>
        </>
      }
      <FormGroup>
        <FormControlLabel
          id='legal-checkbox'
          sx={{
            margin: '0 !important',
            padding: '3px 7.5px',
            gap: '9px'
          }}
          control={
            <Radio
              size='small'
              sx={{padding: '0 !important', width: '18px', height: '18px'}}
              onChange={onChangeHandler('agreedToTerms')}
            />
          }
          label={
            <Typography variant='large500Tight'>
              I have read and agree to the
              {' '}
              <Link href='https://movement-x.com/terms-conditions/' target='_blank'>
                MX Term & Conditions
              </Link>
              <Typography variant='large500Tight' color='text.danger'>{' '}*</Typography>
            </Typography>
          }
        />
      </FormGroup>
      {
        config().turnstilePublicKey
        ? <Stack direction='row' justifyContent='center' alignItems='center' marginTop='16px'>
          <div id="turnstile-container" style={{minHeight: '67px'}}></div>
          </Stack>
        : <></>
      }

      <Stack
        direction='column'
        gap='32px'
        marginTop={0}
      >
        <Button
          variant='primaryGradient'
          sx={{'alignSelf': 'center'}}
          size='medium'
          fullWidth={false}
          disabled={!formIsValid}
          onClick={async () => {
            const isSuccess = await onSubmit(
              isProvider ? omit(formValues, 'hasGuardian') : formValues,
              {hasGuardian,turnstileToken}
            );
            if (!isSuccess && window?.turnstile) window.turnstile.reset();
          }}
        >
          Create Account
        </Button>
      </Stack>
    </FormLayout>
  );
};

NewAccountForm.propTypes = {
  updateField: PropTypes.func, // (fieldName) => value
  onSubmit: PropTypes.func,
};

export default NewAccountForm;
